import { useMemo, useState } from 'react'
import { Autocomplete, Box, TextField, Typography } from '@mui/material'
import { useSelector } from 'react-redux'

import { ManageMembersModal } from '~/entities/assignee-group'
import { providerName } from '~/entities/provider'

interface GroupOption {
  kind: 'Teams' | 'People'
  groupId: number
  label: string
}

/**
 * Ops screen for viewing and editing assignee-group membership. A single search resolves to any
 * editable group: a team (from the assignee-groups slice) or a person's personal group (from the
 * providers slice) — the latter is how a coverage partner adds themselves for someone who is out.
 * Selecting an option opens the shared manage-members modal for that group.
 */
const TeamsCoverageSettings = () => {
  const assigneeGroups = useSelector(state => state.assigneeGroups)
  const providers = useSelector(state => state.providers)
  const [selected, setSelected] = useState<GroupOption | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  const options = useMemo<GroupOption[]>(() => {
    // A retired group is not offered here: its membership no longer needs editing.
    const teams: GroupOption[] = [...assigneeGroups]
      .filter(group => !group.is_deprecated)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(group => ({ kind: 'Teams', groupId: group.id, label: group.name }))
    const people = [...providers]
      .sort((a, b) => providerName(a).localeCompare(providerName(b)))
      .flatMap((provider): GroupOption[] => {
        if (!provider.assignee_group) return []
        return [
          { kind: 'People', groupId: provider.assignee_group.id, label: providerName(provider) },
        ]
      })
    return [...teams, ...people]
  }, [assigneeGroups, providers])

  const handleSelect = (option: GroupOption | null) => {
    setSelected(option)
    if (option) setModalOpen(true)
  }

  // Clear the search selection on close so re-picking the same team/person reopens the modal
  // (Autocomplete's onChange only fires when the value changes).
  const handleClose = () => {
    setModalOpen(false)
    setSelected(null)
  }

  return (
    <Box sx={{ p: 3, width: '100%' }} data-testid="teams-coverage-settings">
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Teams & Coverage
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 2.5 }}>
        View and edit who is in a team, or add yourself to cover for someone who is out of office.
        Pick a team or a person to manage that group&apos;s membership.
      </Typography>

      <Autocomplete
        options={options}
        groupBy={option => option.kind}
        getOptionLabel={option => option.label}
        isOptionEqualToValue={(a, b) => a.groupId === b.groupId}
        value={selected}
        onChange={(_, option) => handleSelect(option)}
        renderInput={params => (
          <TextField {...params} label="Team or person" placeholder="Search" size="small" />
        )}
        sx={{ maxWidth: 480 }}
        data-testid="group-search"
      />

      <ManageMembersModal
        open={modalOpen}
        onClose={handleClose}
        groupId={selected?.groupId ?? null}
        groupName={selected?.label}
      />
    </Box>
  )
}

export default TeamsCoverageSettings
